import { Injectable } from '@angular/core';
import { CanActivate, ActivatedRouteSnapshot, RouterStateSnapshot, Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { UserContextService } from './services/common/user-context.service';
import { RouteAuthorizationData } from './models/authorization/route-authorization-data.interface';
import { AccessType } from './models/authorization/access-type.enum';

/**
 * Guard protecting routes based on user roles and permissions
 * Checks authorization metadata from route.data and blocks access if requirements are not met
 */
@Injectable({
    providedIn: 'root'
})
export class RoleGuard implements CanActivate {

    constructor(
        private router: Router,
        private userContextService: UserContextService
    ) { }

    canActivate(
        route: ActivatedRouteSnapshot,
        state: RouterStateSnapshot
    ): Observable<boolean> | Promise<boolean> | boolean {
        // Get authorization metadata from route.data
        const authData: RouteAuthorizationData = route.data || {};

        // If no requiredPermissionKey, allow access
        if (!authData.requiredPermissionKey) {
            return true;
        }

        // Wait for user context to load
        return this.userContextService.waitForLoad().pipe(
            map(context => {
                // Check if there were errors loading context
                if (context.errors && context.errors.length > 0) {
                    console.error('User context errors:', context.errors);
                    this.navigateToNoAccess(state.url);
                    return false;
                }

                // Check permission requirements
                const requiredAccess = authData.requiredAccess || AccessType.Read;
                const hasAccess = this.userContextService.hasAccess(
                    authData.requiredPermissionKey,
                    requiredAccess
                );

                if (!hasAccess) {
                    this.navigateToNoAccess(state.url);
                    return false;
                }

                return true;
            }),
            catchError(error => {
                console.error('Error checking route access:', error);
                this.navigateToNoAccess(state.url);
                return of(false);
            })
        );
    }

    /**
     * Navigates to no-access page with optional return URL
     */
    private navigateToNoAccess(returnUrl?: string): void {
        const navigationExtras = returnUrl ? { queryParams: { returnUrl } } : {};
        this.router.navigate(['no-access'], navigationExtras);
    }
} 